import React, { useState } from "react";
import { Link } from "react-router-dom";
import Logo from "../atoms/logo";
import { FiAlignJustify } from "react-icons/fi";
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaChevronLeft, FaChevronRight } from "react-icons/fa";

interface PaginationProps {
  currentPage: number;
  totalPage: number;
  handlePageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPage, handlePageChange }) => {
  function getPaginationPages(currentPage: number, totalPages: number, maxVisiblePages = 5): number[] {
    if (totalPages <= maxVisiblePages) {
      // 총 페이지가 5개 이하인 경우: 모든 페이지를 보여줌
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const half = Math.floor(maxVisiblePages / 2);

    if (currentPage <= half) {
      // 현재 페이지가 초반 (1, 2 등)인 경우
      return Array.from({ length: maxVisiblePages }, (_, i) => i + 1);
    }

    if (currentPage > totalPages - half) {
      // 현재 페이지가 마지막 부분인 경우
      return Array.from({ length: maxVisiblePages }, (_, i) => totalPages - maxVisiblePages + i + 1);
    }

    // 현재 페이지가 중간에 위치할 경우
    return Array.from({ length: maxVisiblePages }, (_, i) => currentPage - half + i);
  }

  return (
    <nav className="relative z-0 inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
      <button
        className={`relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-l-md
          ${currentPage === 0 ? "bg-gray-100" : "bg-white hover:bg-gray-50 "}`}
        onClick={() => handlePageChange(0)}
        disabled={currentPage === 0}
      >
        <span className="sr-only">처음</span>
        <FaAngleDoubleLeft />
      </button>
      <button
        className={`relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-l-md
          ${currentPage === 0 ? "bg-gray-100" : "bg-white hover:bg-gray-50 "}`}
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 0}
      >
        <span className="sr-only">이전</span>
        <FaChevronLeft />
      </button>
      {getPaginationPages(currentPage + 1, totalPage).map((num) => (
        <button
          className={`relative inline-flex items-center text-center justify-center px-4 py-2 text-sm font-medium min-w-14 text-gray-700 border border-gray-300 
            ${currentPage + 1 === num ? "bg-gray-100" : "bg-white hover:bg-gray-50 "}`}
          onClick={() => handlePageChange(num - 1)}
          disabled={num === currentPage + 1}
          key={num}
        >
          {num}
        </button>
      ))}
      <button
        className={`relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-r-md 
          ${currentPage + 1 === totalPage ? "bg-gray-100" : "bg-white hover:bg-gray-50 "}`}
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage + 1 === totalPage}
      >
        <span className="sr-only">다음</span>
        <FaChevronRight />
      </button>
      <button
        className={`relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-r-md 
          ${currentPage + 1 === totalPage ? "bg-gray-100" : "bg-white hover:bg-gray-50 "}`}
        onClick={() => handlePageChange(totalPage - 1)}
        disabled={currentPage + 1 === totalPage}
      >
        <span className="sr-only">마지막</span>
        <FaAngleDoubleRight />
      </button>
    </nav>
  );
};

export default Pagination;
